/**
 * CAD paper-space layout store (docs/plans/2d/2d-full-parity-plan.md
 * Faz 9, option A): a layout layer on top of the existing sheet concept.
 *
 * A layout = one paper sheet (named, sized like the sheet templates) holding
 * MVIEW floating viewports. Each viewport frames a region of model space at
 * a scale, clips to its rectangle (optionally a VPCLIP polygon), and carries
 * per-viewport frozen layers (VPLAYER). The layout identity is document
 * state: it round-trips inside the .yap cadScene envelope (see
 * utils/ProjectFileIO restoreCadSideState), not via zustand persist.
 *
 * Paper entities (TEXT/HATCH/TABLE placed on a layout) stay in the CAD
 * ledger tagged `data.onSheet = layoutId`; EntityLayer hides them and
 * PaperSpaceLayer draws them at paper coordinates.
 */

import { create } from 'zustand';
import type { SheetSizeId } from '../core/types';

export interface CadViewport {
    id: string;
    /** Viewport center on paper. */
    cx: number;
    cy: number;
    /** Viewport size on paper. */
    w: number;
    h: number;
    /** Model-space point the viewport looks at. */
    viewCx: number;
    viewCy: number;
    /** Paper units per model unit (1 = 1:1). */
    scale: number;
    /** VPLAYER: layers frozen inside this viewport only. */
    frozenLayers: string[];
    /** VPCLIP: optional polygon boundary (paper coords) further clipping it. */
    clip?: [number, number][];
    /** Show the view title below the frame (2D behavior). */
    showTitle: boolean;
    title: string;
}

export interface CadLayout {
    id: string;
    name: string;
    paper: SheetSizeId;
    /** Paper size in meters. */
    width: number;
    height: number;
    viewports: CadViewport[];
}

interface LayoutState {
    layouts: CadLayout[];
    /** null = MODEL space (the drafting scene); a layout id = that sheet. */
    activeId: string | null;

    setActive: (id: string | null) => void;
    /** Create a layout; name defaults to "Layout N". */
    addLayout: (name?: string, paper?: SheetSizeId, width?: number, height?: number) => CadLayout;
    removeLayout: (id: string) => void;
    renameLayout: (id: string, name: string) => void;
    addViewport: (layoutId: string, vp: Omit<CadViewport, 'id'>) => CadViewport | null;
    updateViewport: (layoutId: string, vpId: string, updates: Partial<CadViewport>) => void;
    removeViewport: (layoutId: string, vpId: string) => void;
    /** Document load: replace the whole registry (cadScene round-trip). */
    replaceAll: (layouts: CadLayout[], activeId: string | null) => void;
    /** Snapshot for saving. */
    serialize: () => { layouts: CadLayout[]; activeId: string | null };
}

export const PAPER_SIZES: Record<string, { w: number; h: number; label: string }> = {
    A0: { w: 1.189, h: 0.841, label: 'A0' },
    A1: { w: 0.841, h: 0.594, label: 'A1' },
    A2: { w: 0.594, h: 0.42, label: 'A2' },
    A3: { w: 0.42, h: 0.297, label: 'A3' },
    A4: { w: 0.297, h: 0.21, label: 'A4' },
    ARCH_D: { w: 0.9144, h: 0.6096, label: 'Arch D' },
    ANSI_B: { w: 0.4318, h: 0.2794, label: 'ANSI B' },
    ANSI_D: { w: 0.5588, h: 0.8636, label: 'ANSI D' },
};

let vpCounter = 0;

export const useLayoutStore = create<LayoutState>((set, get) => ({
    layouts: [],
    activeId: null,

    setActive: (id) => set({ activeId: id }),

    addLayout: (name, paper = 'A3', width, height) => {
        const n = get().layouts.length + 1;
        const size = PAPER_SIZES[paper] ?? PAPER_SIZES.A3;
        const layout: CadLayout = {
            id: `layout-${Date.now().toString(36)}-${n}`,
            name: name?.trim() || `Layout ${n}`,
            paper,
            width: width ?? size.w,
            height: height ?? size.h,
            viewports: [],
        };
        set((s) => ({ layouts: [...s.layouts, layout] }));
        return layout;
    },

    removeLayout: (id) => set((s) => ({
        layouts: s.layouts.filter((l) => l.id !== id),
        activeId: s.activeId === id ? null : s.activeId,
    })),

    renameLayout: (id, name) => set((s) => ({
        layouts: s.layouts.map((l) => (l.id === id ? { ...l, name } : l)),
    })),

    addViewport: (layoutId, vp) => {
        const layout = get().layouts.find((l) => l.id === layoutId);
        if (!layout) return null;
        const created: CadViewport = { ...vp, id: `vp-${Date.now().toString(36)}-${vpCounter++}` };
        set((s) => ({
            layouts: s.layouts.map((l) => (l.id === layoutId ? { ...l, viewports: [...l.viewports, created] } : l)),
        }));
        return created;
    },

    updateViewport: (layoutId, vpId, updates) => set((s) => ({
        layouts: s.layouts.map((l) => (l.id !== layoutId ? l : {
            ...l,
            viewports: l.viewports.map((v) => (v.id === vpId ? { ...v, ...updates } : v)),
        })),
    })),

    removeViewport: (layoutId, vpId) => set((s) => ({
        layouts: s.layouts.map((l) => (l.id !== layoutId ? l : {
            ...l,
            viewports: l.viewports.filter((v) => v.id !== vpId),
        })),
    })),

    replaceAll: (layouts, activeId) => set({ layouts, activeId }),

    serialize: () => ({ layouts: get().layouts, activeId: get().activeId }),
}));
